import Layout from "@/components/LandingPage/Layout";
import { Typography } from "antd";
import React, { useState, useEffect, useMemo } from "react";
import { PRIVACY_HTML } from "@/utils/policy-content";

const { Title, Text } = Typography;

const EFFECTIVE_DATE = "June 9, 2026";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("");

  // TOC derived from the content's <h2 id="..."> headings so it always matches.
  const tableOfContents = useMemo(() => {
    const items: { key: string; title: string; href: string }[] = [];
    const re = /<h2[^>]*id="([^"]+)"[^>]*>([\s\S]*?)<\/h2>/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(PRIVACY_HTML)) !== null) {
      const title = m[2].replace(/<[^>]+>/g, "").trim();
      items.push({ key: m[1], title, href: `#${m[1]}` });
    }
    return items;
  }, []);

  useEffect(() => {
    if (!tableOfContents.length) return;
    const sections = tableOfContents.map((item) => item.key);
    const handleScroll = () => {
      const headerHeight = document.querySelector("header")?.offsetHeight || 80;
      const scrollPosition = window.scrollY + headerHeight + 100;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (window.scrollY + windowHeight >= documentHeight - 100) {
        setActiveSection(sections[sections.length - 1]);
        return;
      }

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tableOfContents]);

  return (
    <Layout>
      <section className="py-10 bg-gray-50 min-h-screen">
        <div className="w-full mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Title
              style={{ color: "#2D68C4" }}
              className="!text-[28px] sm:!text-[36px] md:!text-[42px] !mb-4"
            >
              Privacy Policy
            </Title>
            <Text className="text-lg text-gray-600">
              How we collect, use, and protect your information
            </Text>
            <div className="mt-4">
              <Text className="text-gray-500">
                Effective Date: {EFFECTIVE_DATE}
              </Text>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
            {/* Table of Contents - Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-24">
                <Title level={4} className="!mb-4 !text-[#2D68C4]">
                  Table of Contents
                </Title>
                <div className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <a
                      key={item.key}
                      href={item.href}
                      className={`block py-2 px-3 rounded-md text-sm transition-colors duration-200 ${
                        activeSection === item.key
                          ? "bg-blue-50 text-[#2D68C4] font-medium border-l-3 border-[#2D68C4]"
                          : "text-gray-600 hover:text-[#2D68C4] hover:bg-gray-50"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSection(item.key);
                        const targetElement = document.getElementById(item.key);
                        if (targetElement) {
                          const headerHeight =
                            document.querySelector("header")?.offsetHeight || 80;
                          window.scrollTo({
                            top: targetElement.offsetTop - headerHeight - 20,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      {`${index + 1}. ${item.title}`}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
              <div
                className="bg-white rounded-lg shadow-sm border p-8 policy-content"
                dangerouslySetInnerHTML={{ __html: PRIVACY_HTML }}
              />
            </div>
          </div>
        </div>
      </section>

      <style jsx global>{`
        .policy-content h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #2d68c4;
          margin: 1.75rem 0 0.75rem;
        }
        .policy-content h3 {
          font-size: 1.05rem;
          font-weight: 600;
          color: #1f2937;
          margin: 1.25rem 0 0.5rem;
        }
        .policy-content p {
          color: #374151;
          line-height: 1.7;
          margin: 0 0 0.85rem;
        }
        .policy-content ul {
          list-style: disc;
          margin: 0 0 0.85rem 1.5rem;
          color: #374151;
          line-height: 1.7;
        }
        .policy-content li {
          margin: 0 0 0.4rem;
        }
      `}</style>
    </Layout>
  );
};

export default PrivacyPolicy;
